import {Injectable, PLATFORM_ID, inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, catchError, map, shareReplay, throwError} from 'rxjs';
import {EnvironmentUrlService} from './environment-url.service';

export type StrengthTone = 'primary' | 'success' | 'warning' | 'info' | 'dark';

export interface ModelStrength {
  label: string;
  icon: string; // mdi icon name without the `mdi-` prefix
  tone: StrengthTone;
}

export interface Model {
  name: string;
  value: string;             // OpenRouter model id
  provider: string;
  description: string;
  cost: number;              // 1-5 overall cost tier (used for $ icons)
  strengths: ModelStrength[];
  // Live metadata from OpenRouter (via the backend catalog); undefined until loaded.
  inputPricePerMillion?: number;
  outputPricePerMillion?: number;
  contextWindow?: number;
}

/** Shape returned by `GET api/models?ids=...`. */
interface ModelInfoDto {
  id: string;
  name: string;
  inputPricePerMillionTokens: number;
  outputPricePerMillionTokens: number;
  contextLength: number;
}

/** Shape returned by `GET api/workspaces/models/agents`: models that can call tools. */
export interface AgentModelDto extends ModelInfoDto {
  supportsImages: boolean;
  supportsFiles: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ModelService {
  private agentCatalog$?: Observable<AgentModelDto[]>;
  private readonly DEFAULT_MODEL_VALUE = 'google/gemini-3.7-flash';

  // Models retired from the picker, mapped to their replacement. A stored selection
  // is upgraded rather than falling back to the default model.
  private readonly RETIRED_MODEL_VALUES: Record<string, string> = {
    'anthropic/claude-opus-5': 'anthropic/claude-opus-5.5',
    'anthropic/claude-fable-5': 'anthropic/claude-opus-5.5',
    'anthropic/claude-opus-4.8': 'anthropic/claude-opus-5.5',
    'anthropic/claude-sonnet-5': 'openai/gpt-6.1-sol',
    'openai/gpt-5.6-terra': 'openai/gpt-6.1-sol',
    'openai/gpt-5.6-sol': 'openai/gpt-6.1-sol',
    'openai/gpt-6-luna': 'openai/gpt-6.1-sol',
    'openai/gpt-6-astra': 'openai/gpt-6.1-sol',
    'openai/gpt-6-sol': 'openai/gpt-6.1-sol',
    'qwen/qwen3.8-max-0902': 'xiaomi/mimo-v2.6-pro',
    'google/gemini-3.1-pro-preview': 'google/gemini-3.7-flash',
    'google/gemini-3.6-flash': 'google/gemini-3.7-flash',
    'mistralai/mistral-medium-3-5': 'z-ai/glm-5.3',
    'z-ai/glm-5.2': 'z-ai/glm-5.3-flash',
    'deepseek/deepseek-v4-pro': 'z-ai/glm-5.3-flash',
    'deepseek/deepseek-v4-flash': 'deepseek/deepseek-v4.1-flash'
  };

  models: Model[] = [
    {
      name: 'Claude Opus 5.5',
      value: 'anthropic/claude-opus-5.5',
      provider: 'Anthropic',
      description: 'The #1 model for prose on the writing leaderboards and the top-rated fiction craft overall. Unmatched voice, subtext, and character work: reach for it on the chapters that matter most.',
      cost: 5,
      strengths: [
        { label: 'Best prose', icon: 'trophy', tone: 'primary' },
        { label: 'Literary quality', icon: 'feather', tone: 'warning' }
      ]
    },
    {
      name: 'Kimi K3',
      value: 'moonshotai/kimi-k3',
      provider: 'Moonshot',
      description: '#2 on the creative writing leaderboard with a distinctive, less formulaic voice and a 1M-token window. The alternative when frontier models feel too polished or generic.',
      cost: 4,
      strengths: [
        { label: 'Distinctive voice', icon: 'drama-masks', tone: 'primary' },
        { label: 'Less formulaic', icon: 'shuffle-variant', tone: 'info' }
      ]
    },
    {
      name: 'GPT-6.1 Sol',
      value: 'openai/gpt-6.1-sol',
      provider: 'OpenAI',
      description: 'OpenAI\'s latest GPT-6.1 model for drafting and revision, with a 1M-token context window for your manuscript, story notes, and instructions.',
      cost: 3,
      strengths: [
        { label: 'Versatile', icon: 'palette', tone: 'primary' },
        { label: '1M context', icon: 'book-open-page-variant', tone: 'info' }
      ]
    },
    {
      name: 'MiMo-V2.6-Pro',
      value: 'xiaomi/mimo-v2.6-pro',
      provider: 'Xiaomi',
      description: 'Xiaomi\'s flagship MiMo model, with a 1M-token context window for your manuscript, story notes, and detailed revision instructions.',
      cost: 2,
      strengths: [
        { label: '1M context', icon: 'book-open-page-variant', tone: 'primary' },
        { label: 'Long documents', icon: 'file-document-outline', tone: 'info' }
      ]
    },
    {
      name: 'GLM 5.3',
      value: 'z-ai/glm-5.3',
      provider: 'Z.ai',
      description: '#3 on the creative writing leaderboard and #1 for long-form novels, with the least clichéd prose of the frontier group — at a fraction of the price. Open-weight and lightly moderated.',
      cost: 2,
      strengths: [
        { label: 'Best long-form', icon: 'book-open-page-variant', tone: 'primary' },
        { label: 'Frontier value', icon: 'diamond-stone', tone: 'success' }
      ]
    },
    {
      name: 'Gemini 3.7 Flash',
      value: 'google/gemini-3.7-flash',
      provider: 'Google',
      description: 'Top-3 on Chatbot Arena creative writing at a budget price. Fast, structurally disciplined drafting that keeps plots coherent over thousands of words. The ideal default for everyday work.',
      cost: 2,
      strengths: [
        { label: 'Fast', icon: 'lightning-bolt', tone: 'warning' },
        { label: 'Arena top 3', icon: 'chart-line', tone: 'info' }
      ]
    },
    {
      name: 'GLM 5.3 Flash',
      value: 'z-ai/glm-5.3-flash',
      provider: 'Z.ai',
      description: 'Near-frontier writing quality — within reach of GPT-5.6 Sol on the leaderboards — at a rock-bottom price, with a 1M-token window. The standout budget pick for drafting at scale.',
      cost: 1,
      strengths: [
        { label: 'Near-frontier', icon: 'star-four-points', tone: 'primary' },
        { label: 'Ultra-cheap', icon: 'cash', tone: 'success' }
      ]
    },
    {
      name: 'DeepSeek V4.1 Flash',
      value: 'deepseek/deepseek-v4.1-flash',
      provider: 'DeepSeek',
      description: 'DeepSeek\'s newest Flash model: fast, inexpensive and reliable with tools, with a 1M-token window. The default for agents at the desk.',
      cost: 1,
      strengths: [
        { label: 'Fast', icon: 'lightning-bolt', tone: 'warning' },
        { label: '1M context', icon: 'book-open-page-variant', tone: 'info' }
      ]
    }
  ];

  /**
   * The models a Desk agent can run on, picked for agent work: fast, reliable with tools across long runs,
   * and good at prose. Prices come live from the catalog; anything else is under "Any OpenRouter model…".
   * The first is the default (`OpenRouterModelIds.DeskWriter`).
   */
  agentModels: Model[] = [
    {
      name: 'Claude Haiku 5.5',
      value: 'anthropic/claude-haiku-5.5',
      provider: 'Anthropic',
      description: 'Fast and inexpensive, steady with tools over long tasks, with Claude\'s ear for prose. The default for agents at the desk.',
      cost: 1,
      strengths: [
        { label: 'Fast', icon: 'lightning-bolt', tone: 'warning' },
        { label: 'Good prose', icon: 'feather', tone: 'primary' }
      ]
    },
    {
      name: 'Claude Sonnet 5.5',
      value: 'anthropic/claude-sonnet-5.5',
      provider: 'Anthropic',
      description: 'Careful over long, many-step tasks, with strong prose. About twenty times the price of Haiku.',
      cost: 4,
      strengths: [
        { label: 'Most capable', icon: 'trophy', tone: 'primary' },
        { label: 'Best prose', icon: 'feather', tone: 'warning' }
      ]
    },
    {
      name: 'Claude Opus 5.5',
      value: 'anthropic/claude-opus-5.5',
      provider: 'Anthropic',
      description: 'For demanding drafting and revision, with careful prose and character work. A premium option for the chapters that matter most.',
      cost: 5,
      strengths: [
        { label: 'Literary quality', icon: 'feather', tone: 'primary' },
        { label: 'Detailed revision', icon: 'book-open-page-variant', tone: 'info' }
      ]
    },
    {
      name: 'Muse Wonder',
      value: 'meta/muse-wonder',
      provider: 'Meta',
      description: 'Built for complex reasoning and long, multi-step tasks with tools, with a 1M-token context window for your manuscript and story notes.',
      cost: 5,
      strengths: [
        { label: 'Agent work', icon: 'tools', tone: 'primary' },
        { label: '1M context', icon: 'book-open-page-variant', tone: 'info' }
      ]
    },
    {
      name: 'GPT-6.1 Sol',
      value: 'openai/gpt-6.1-sol',
      provider: 'OpenAI',
      description: 'Drafting and revision with tools, with a 1M-token context window for your manuscript, story notes, and instructions.',
      cost: 3,
      strengths: [
        { label: 'Versatile', icon: 'palette', tone: 'primary' },
        { label: '1M context', icon: 'book-open-page-variant', tone: 'info' }
      ]
    },
    {
      name: 'Gemini 4 Argon',
      value: 'google/gemini-4-argon',
      provider: 'Google',
      description: 'Built for multi-step agent work and complex reasoning, with a 1M-token context window for long manuscripts and story notes.',
      cost: 3,
      strengths: [
        { label: 'Agent work', icon: 'tools', tone: 'primary' },
        { label: '1M context', icon: 'book-open-page-variant', tone: 'info' }
      ]
    },
    {
      name: 'Gemini 3.8 Flash',
      value: 'google/gemini-3.8-flash',
      provider: 'Google',
      description: 'Google\'s quickest strong model, good at multi-step work and with long manuscripts. Also the stand-in when another model is down.',
      cost: 2,
      strengths: [
        { label: 'Very fast', icon: 'lightning-bolt', tone: 'warning' },
        { label: '1M context', icon: 'book-open-page-variant', tone: 'info' }
      ]
    },
    {
      name: 'DeepSeek V4.1 Flash',
      value: 'deepseek/deepseek-v4.1-flash',
      provider: 'DeepSeek',
      description: 'Cheap, quick and dependable with tools: it finished every task in our tests without a failed call. Plainer prose than Claude.',
      cost: 1,
      strengths: [
        { label: 'Fast', icon: 'lightning-bolt', tone: 'warning' },
        { label: 'Reliable tools', icon: 'check-circle-outline', tone: 'success' }
      ]
    },
    {
      name: 'GLM 5.3 FlashX',
      value: 'z-ai/glm-5.3-flashx',
      provider: 'Z.ai',
      description: 'The high-speed GLM 5.3 Flash, at up to 200 tokens a second, and strong with tools. Good for quick passes over many pages.',
      cost: 2,
      strengths: [
        { label: 'Very fast', icon: 'lightning-bolt', tone: 'warning' },
        { label: 'Strong tools', icon: 'tools', tone: 'info' }
      ]
    }
  ];

  private modelMap: Map<string, Model>;

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private currentModelSubject: BehaviorSubject<Model>;
  public currentModel$: Observable<Model>;

  constructor(
    private http: HttpClient,
    private envUrl: EnvironmentUrlService
  ) {
    this.modelMap = new Map(this.models.map(model => [model.value, model]));

    const storedModelValue = this.isBrowser ? localStorage.getItem('selectedModel') : null;
    let initialModel: Model | undefined;

    if (storedModelValue) {
      const resolvedModelValue = this.RETIRED_MODEL_VALUES[storedModelValue] || storedModelValue;
      initialModel = this.modelMap.get(resolvedModelValue);
    }

    if (!initialModel) {
      initialModel = this.modelMap.get(this.DEFAULT_MODEL_VALUE) || this.models[0];
    }

    if (this.isBrowser && initialModel.value !== storedModelValue) {
      localStorage.setItem('selectedModel', initialModel.value);
    }

    this.currentModelSubject = new BehaviorSubject<Model>(initialModel);
    this.currentModel$ = this.currentModelSubject.asObservable();

    // Prerendered pages only show the editorial copy; live prices load once the page runs in a browser.
    if (this.isBrowser) {
      this.loadLiveModelInfo();
    }
  }

  /**
   * Prices and context windows come from OpenRouter's live listing (through the backend, so the
   * picker shows the same numbers generation is charged against). The editorial copy stays local.
   */
  private loadLiveModelInfo(): void {
    const ids = this.models.map(model => model.value).join(',');
    this.http.get<ModelInfoDto[]>(`${this.envUrl.apiUrl}/models`, { params: { ids } }).subscribe({
      next: infos => {
        for (const info of infos) {
          const model = this.modelMap.get(info.id);
          if (!model) {
            continue;
          }
          model.inputPricePerMillion = info.inputPricePerMillionTokens;
          model.outputPricePerMillion = info.outputPricePerMillionTokens;
          model.contextWindow = info.contextLength;
        }
        // Re-emit so subscribers (e.g. the context usage meter) pick up the loaded context window.
        this.currentModelSubject.next(this.currentModelSubject.value);
      },
      error: error => console.warn('Could not load live model info from the backend', error)
    });
  }

  getModels(): Model[] {
    return this.models;
  }

  /**
   * The Desk's agent models (`agentModels`), as far as OpenRouter lists them as tool-capable, with live
   * prices. Desk flag only; the endpoint answers 404 without it.
   */
  getAgentModels(): Observable<Model[]> {
    return this.getAllAgentModels().pipe(
      map(infos => {
        const byId = new Map(infos.map(info => [info.id, info]));
        return this.agentModels
          .filter(model => byId.has(model.value))
          .map(model => {
            const info = byId.get(model.value)!;
            return {
              ...model,
              inputPricePerMillion: info.inputPricePerMillionTokens,
              outputPricePerMillion: info.outputPricePerMillionTokens,
              contextWindow: info.contextLength
            };
          });
      })
    );
  }

  /**
   * Every model a Desk agent can run on: all that OpenRouter lists as tool-capable, with live prices.
   * Asked for once and shared; a failed request is asked for again next time.
   */
  getAllAgentModels(): Observable<AgentModelDto[]> {
    this.agentCatalog$ ??= this.http.get<AgentModelDto[]>(`${this.envUrl.apiUrl}/workspaces/models/agents`).pipe(
      catchError(error => {
        this.agentCatalog$ = undefined;
        return throwError(() => error);
      }),
      shareReplay(1)
    );
    return this.agentCatalog$;
  }

  getCurrentModel(): Model {
    return this.currentModelSubject.value;
  }

  setCurrentModel(model: Model): void {
    this.currentModelSubject.next(model);
    localStorage.setItem('selectedModel', model.value);
  }
}
